import {
  CalendarDays,
  CalendarRange,
  ChevronDown,
  Clock,
  type LucideIcon,
  Settings2,
  Sun
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export interface CronFields {
  minute: string
  hour: string
  day: string
  month: string
  day_of_week: string
}

type Mode = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'

const MODES: Mode[] = ['hourly', 'daily', 'weekly', 'monthly', 'custom']
const MODE_ICONS: Record<Mode, LucideIcon> = {
  hourly: Clock,
  daily: Sun,
  weekly: CalendarDays,
  monthly: CalendarRange,
  custom: Settings2
}
const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const RAW_FIELDS: Array<keyof CronFields> = [
  'minute',
  'hour',
  'day',
  'month',
  'day_of_week'
]
const RAW_FIELD_LABELS: Record<keyof CronFields, string> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  month: 'month',
  day_of_week: 'dayOfWeek'
}

const isNum = (value: string) => /^\d+$/.test(value)
const pad = (value: string) => (isNum(value) ? value.padStart(2, '0') : value)
const num = (value: string, fallback: string) => (isNum(value) ? value : fallback)

function parseWeekdays(dow: string): string[] {
  if (!dow || dow === '*') return []
  return dow
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => WEEKDAYS.includes(token))
}

function inferMode(value: CronFields): Mode {
  const starDate =
    value.day === '*' && value.month === '*' && value.day_of_week === '*'
  if (starDate && value.hour === '*' && isNum(value.minute)) return 'hourly'
  if (starDate && isNum(value.hour) && isNum(value.minute)) return 'daily'
  if (
    value.day === '*' &&
    value.month === '*' &&
    parseWeekdays(value.day_of_week).length > 0 &&
    isNum(value.hour) &&
    isNum(value.minute)
  )
    return 'weekly'
  if (
    isNum(value.day) &&
    value.month === '*' &&
    value.day_of_week === '*' &&
    isNum(value.hour) &&
    isNum(value.minute)
  )
    return 'monthly'
  return 'custom'
}

function range(n: number, start = 0): string[] {
  return Array.from({ length: n }, (_, i) => String(i + start))
}

interface SelectProps {
  value: string
  options: string[]
  onChange: (value: string) => void
  ariaLabel?: string
}

function TimeSelect({ value, options, onChange, ariaLabel }: SelectProps) {
  return (
    <div className="relative">
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-11 w-20 appearance-none rounded-xl border border-ink/10 bg-white bg-none pl-3.5 pr-8 text-center font-display text-lg font-bold tabular-nums text-ink transition-colors [-moz-appearance:none] [-webkit-appearance:none] [&::-ms-expand]:hidden hover:border-ink/20 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {pad(option)}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/35" />
    </div>
  )
}

interface Props {
  value: CronFields
  onChange: (value: CronFields) => void
}

export function CronEditor({ value, onChange }: Props) {
  const { t } = useTranslation('cron')
  const [forceCustom, setForceCustom] = useState(false)
  const mode: Mode = forceCustom ? 'custom' : inferMode(value)

  const hour = num(value.hour, '0')
  const minute = num(value.minute, '0')
  const day = num(value.day, '1')
  const selectedDays = parseWeekdays(value.day_of_week)

  function selectMode(next: Mode) {
    if (next === 'custom') {
      setForceCustom(true)
      return
    }
    setForceCustom(false)
    if (next === 'hourly') {
      onChange({ minute, hour: '*', day: '*', month: '*', day_of_week: '*' })
    } else if (next === 'daily') {
      onChange({ minute, hour, day: '*', month: '*', day_of_week: '*' })
    } else if (next === 'weekly') {
      onChange({
        minute,
        hour,
        day: '*',
        month: '*',
        day_of_week: selectedDays.length ? selectedDays.join(',') : 'mon'
      })
    } else if (next === 'monthly') {
      onChange({ minute, hour, day, month: '*', day_of_week: '*' })
    }
  }

  function setTime(nextHour: string, nextMinute: string) {
    onChange({ ...value, hour: nextHour, minute: nextMinute })
  }

  function toggleWeekday(weekday: string) {
    const next = selectedDays.includes(weekday)
      ? selectedDays.filter((d) => d !== weekday)
      : [...selectedDays, weekday]
    if (next.length === 0) return // keep at least one day selected
    const ordered = WEEKDAYS.filter((d) => next.includes(d))
    onChange({ ...value, day_of_week: ordered.join(',') })
  }

  const time = `${pad(hour)}:${pad(minute)}`
  const cronString = `${value.minute} ${value.hour} ${value.day} ${value.month} ${value.day_of_week}`

  let summary: string
  if (mode === 'hourly') {
    summary = t('editor.summary.hourly', { minute: Number(minute) })
  } else if (mode === 'daily') {
    summary = t('editor.summary.daily', { time })
  } else if (mode === 'weekly') {
    const days = selectedDays.map((d) => t(`editor.weekdays.${d}`)).join(', ')
    summary = t('editor.summary.weekly', { days, time })
  } else if (mode === 'monthly') {
    summary = t('editor.summary.monthly', { day, time })
  } else {
    summary = t('editor.summary.custom', { cron: cronString })
  }

  const SummaryIcon = MODE_ICONS[mode]

  return (
    <div className="space-y-5">
      {/* Schedule-type selector */}
      <div className="inline-flex flex-wrap gap-1 rounded-2xl bg-ink/[0.04] p-1">
        {MODES.map((m) => {
          const Icon = MODE_ICONS[m]
          const active = mode === m
          return (
            <button
              key={m}
              type="button"
              onClick={() => selectMode(m)}
              aria-pressed={active}
              className={cn(
                'inline-flex cursor-pointer appearance-none items-center gap-1.5 rounded-xl border-0 px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                active
                  ? 'bg-white text-primary shadow-sm'
                  : 'bg-transparent text-ink/55 hover:text-ink'
              )}
            >
              <Icon
                className={cn('h-4 w-4', active ? 'text-primary' : 'text-ink/40')}
              />
              {t(`editor.types.${m}`)}
            </button>
          )
        })}
      </div>

      {/* Per-mode controls */}
      {mode === 'hourly' && (
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-ink/70">
            {t('editor.atMinute')}
          </span>
          <TimeSelect
            value={minute}
            options={range(60)}
            onChange={(m) => onChange({ ...value, minute: m })}
            ariaLabel={t('editor.atMinute')}
          />
        </div>
      )}

      {(mode === 'daily' || mode === 'weekly' || mode === 'monthly') && (
        <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
          {mode === 'monthly' && (
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-ink/45">
                {t('editor.onDay')}
              </span>
              <TimeSelect
                value={day}
                options={range(31, 1)}
                onChange={(d) => onChange({ ...value, day: d })}
                ariaLabel={t('editor.onDay')}
              />
            </label>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-ink/45">
              {t('editor.atTime')}
            </span>
            <div className="flex items-center gap-1.5">
              <TimeSelect
                value={hour}
                options={range(24)}
                onChange={(h) => setTime(h, minute)}
                ariaLabel={t('fields.hour')}
              />
              <span className="font-display text-lg font-bold text-ink/30">
                :
              </span>
              <TimeSelect
                value={minute}
                options={range(60)}
                onChange={(m) => setTime(hour, m)}
                ariaLabel={t('fields.minute')}
              />
            </div>
          </label>
        </div>
      )}

      {mode === 'weekly' && (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-ink/45">
            {t('editor.onDays')}
          </span>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((weekday) => {
              const active = selectedDays.includes(weekday)
              return (
                <button
                  key={weekday}
                  type="button"
                  onClick={() => toggleWeekday(weekday)}
                  aria-pressed={active}
                  className={cn(
                    'h-10 w-12 cursor-pointer appearance-none rounded-xl border-0 text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                    active
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-ink/[0.04] text-ink/55 hover:bg-ink/[0.08] hover:text-ink'
                  )}
                >
                  {t(`editor.weekdays.${weekday}`)}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {mode === 'custom' && (
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
          {RAW_FIELDS.map((field) => (
            <label key={field} className="flex flex-col gap-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-ink/45">
                {t(`fields.${RAW_FIELD_LABELS[field]}`)}
              </span>
              <Input
                value={value[field]}
                className="text-center font-mono"
                onChange={(e) => onChange({ ...value, [field]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}

      {/* Signature: the schedule in plain language */}
      <div className="flex items-center gap-3.5 rounded-2xl bg-primary/[0.05] px-4 py-3.5 ring-1 ring-inset ring-primary/10">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <SummaryIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="font-display text-base font-bold leading-tight text-ink">
            {summary}
          </div>
          <code className="text-xs tracking-wide text-ink/40">{cronString}</code>
        </div>
      </div>
    </div>
  )
}
